import React from "react";
import InputBase from "@material-ui/core/InputBase";
import SearchIcon from "@material-ui/icons/Search";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import { inject, observer } from "mobx-react";
import { withRouter } from "react-router";
import classnames from "classnames";
import withCustomStyles from "./SearchPart.style";
import { getImage, getSearchPageUrl } from "../utils/UrlUtils";
import { searchSuggestionRequest } from "../utils/RestMiddlewareUtil";

const LISTBOX_ID = "header-search-suggestions";
const SUGGESTION_LIMIT = 6;
const DEBOUNCE_MS = 300;
const PLACEHOLDERS = ["Search eggs", "Search milk", "Search chocolates"];
const PLACEHOLDER_INTERVAL_MS = 2500;
const PLACEHOLDER_SLIDE_MS = 400;

const suggestionLabel = suggestion => suggestion.name || suggestion.title;

// Header search: grey pill with a leading icon and an autocomplete list.
// Enter searches (or opens the highlighted suggestion); ↑/↓ move, Esc closes.
@inject(["searchProductsStore"])
@observer
class SearchPart extends React.Component {
  state = {
    query: "",
    suggestions: [],
    showSuggestions: false,
    loading: false,
    selectedIndex: -1,
    placeholderIndex: 0,
    placeholderAnimate: true
  };

  searchTimeout = null;

  placeholderTimer = null;

  placeholderResetTimer = null;

  componentDidMount() {
    this.placeholderTimer = setInterval(
      this.advancePlaceholder,
      PLACEHOLDER_INTERVAL_MS
    );
  }

  componentWillUnmount() {
    clearTimeout(this.searchTimeout);
    clearInterval(this.placeholderTimer);
    clearTimeout(this.placeholderResetTimer);
  }

  // Slides the placeholder stack up one row. The list ends with a copy of the
  // first item, so after sliding onto it we snap back to 0 without a transition
  // for a seamless loop.
  advancePlaceholder = () => {
    if (this.state.query.length > 0) return;
    const next = this.state.placeholderIndex + 1;
    this.setState({ placeholderIndex: next, placeholderAnimate: true });
    if (next === PLACEHOLDERS.length) {
      clearTimeout(this.placeholderResetTimer);
      this.placeholderResetTimer = setTimeout(
        () => this.setState({ placeholderIndex: 0, placeholderAnimate: false }),
        PLACEHOLDER_SLIDE_MS
      );
    }
  };

  renderPlaceholder() {
    const { classes } = this.props;
    const { query, placeholderIndex, placeholderAnimate } = this.state;
    if (query.length > 0) return null;
    return (
      <span className={classes.placeholder} aria-hidden="true">
        <span
          className={classnames(classes.placeholderTrack, {
            [classes.placeholderAnimate]: placeholderAnimate
          })}
          style={{ transform: `translateY(-${placeholderIndex * 24}px)` }}
        >
          {[...PLACEHOLDERS, PLACEHOLDERS[0]].map((text, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <span key={i} className={classes.placeholderItem}>
              {text}
            </span>
          ))}
        </span>
      </span>
    );
  }

  handleInputChange = event => {
    const query = event.target.value;
    this.setState({
      query,
      showSuggestions: query.length > 0,
      selectedIndex: -1
    });

    clearTimeout(this.searchTimeout);
    if (query.length > 0) {
      this.searchTimeout = setTimeout(
        () => this.fetchSuggestions(query),
        DEBOUNCE_MS
      );
    } else {
      this.setState({ suggestions: [], loading: false });
    }
  };

  fetchSuggestions = async query => {
    this.setState({ loading: true });
    const tempStore = { state: "pending", promise: null };
    try {
      await searchSuggestionRequest(
        tempStore,
        { query, limit: SUGGESTION_LIMIT },
        data => {
          // ignore responses for a query the user has already changed
          if (query !== this.state.query) return;
          const products = Array.isArray(data) ? data : [];
          this.setState({
            suggestions: products.slice(0, SUGGESTION_LIMIT),
            loading: false
          });
        },
        () => this.setState({ loading: false })
      );
    } catch (error) {
      this.setState({ loading: false });
    }
  };

  handleSuggestionClick = suggestion => {
    const label = suggestionLabel(suggestion);
    this.setState({
      query: label,
      showSuggestions: false,
      suggestions: [],
      selectedIndex: -1
    });
    this.props.history.push(getSearchPageUrl(label));
  };

  handleInputFocus = () => {
    if (this.state.query.length > 0) {
      this.setState({ showSuggestions: true });
    }
  };

  handleClickAway = () => {
    this.setState({ showSuggestions: false, selectedIndex: -1 });
  };

  doSearch = () => {
    const query = this.state.query.trim();
    if (query) {
      this.setState({ showSuggestions: false, selectedIndex: -1 });
      this.props.history.push(getSearchPageUrl(query));
    }
  };

  handleKeyDown = event => {
    const { suggestions, selectedIndex, showSuggestions } = this.state;
    const open = showSuggestions && suggestions.length > 0;

    switch (event.key) {
      case "Enter":
        event.preventDefault();
        if (open && selectedIndex >= 0 && suggestions[selectedIndex]) {
          this.handleSuggestionClick(suggestions[selectedIndex]);
        } else {
          this.doSearch();
        }
        break;
      case "ArrowDown":
        if (open) {
          event.preventDefault();
          this.setState({
            selectedIndex: Math.min(selectedIndex + 1, suggestions.length - 1)
          });
        }
        break;
      case "ArrowUp":
        if (open) {
          event.preventDefault();
          this.setState({ selectedIndex: Math.max(selectedIndex - 1, -1) });
        }
        break;
      case "Escape":
        this.setState({ showSuggestions: false, selectedIndex: -1 });
        break;
      default:
        break;
    }
  };

  renderSuggestions() {
    const {
      props: { classes },
      state: { query, suggestions, loading, selectedIndex }
    } = this;

    if (loading && suggestions.length === 0) {
      return <div className={classes.status}>Searching…</div>;
    }
    if (suggestions.length === 0) {
      return query.length > 0 ? (
        <div className={classes.status}>
          No suggestions. Press Enter to search for “{query}”.
        </div>
      ) : null;
    }
    return (
      <ul id={LISTBOX_ID} role="listbox" className={classes.list}>
        {suggestions.map((suggestion, index) => {
          const label = suggestionLabel(suggestion);
          return (
            <li
              key={suggestion.id || index}
              id={`${LISTBOX_ID}-${index}`}
              role="option"
              aria-selected={index === selectedIndex}
              className={classnames(classes.option, {
                [classes.optionActive]: index === selectedIndex
              })}
              onMouseDown={e => e.preventDefault()}
              onClick={() => this.handleSuggestionClick(suggestion)}
              onKeyDown={() => {}}
            >
              <span className={classes.thumb}>
                <img
                  alt=""
                  src={
                    suggestion.image_v2 ||
                    suggestion.image ||
                    getImage(suggestion)
                  }
                  width="36"
                  height="36"
                />
              </span>
              <span className={classes.optionText}>{label}</span>
            </li>
          );
        })}
      </ul>
    );
  }

  render() {
    const {
      props: { classes, className },
      state: { query, suggestions, showSuggestions, selectedIndex }
    } = this;
    const expanded = showSuggestions;

    return (
      <ClickAwayListener onClickAway={this.handleClickAway}>
        <form
          role="search"
          className={classnames(classes.search, className)}
          onSubmit={e => {
            e.preventDefault();
            this.doSearch();
          }}
        >
          <label htmlFor="header-search" className={classes.pill}>
            <SearchIcon className={classes.icon} aria-hidden="true" />
            <span className={classes.inputWrap}>
              {this.renderPlaceholder()}
              <InputBase
                id="header-search"
                type="search"
                value={query}
                onChange={this.handleInputChange}
                onKeyDown={this.handleKeyDown}
                onFocus={this.handleInputFocus}
                autoComplete="off"
                inputProps={{
                  "aria-label": "Search products",
                  role: "combobox",
                  "aria-expanded": expanded && suggestions.length > 0,
                  "aria-controls": LISTBOX_ID,
                  "aria-autocomplete": "list",
                  "aria-activedescendant":
                    selectedIndex >= 0
                      ? `${LISTBOX_ID}-${selectedIndex}`
                      : undefined,
                  enterKeyHint: "search"
                }}
                classes={{ root: classes.inputRoot, input: classes.inputInput }}
              />
            </span>
          </label>
          {expanded && (
            <div className={classes.dropdown}>{this.renderSuggestions()}</div>
          )}
        </form>
      </ClickAwayListener>
    );
  }
}

export default withRouter(withCustomStyles(SearchPart));
